import { useState, useEffect, useCallback } from 'react';
import { trackerAPI } from '../api/tracker.api';
import type {
  MessageTemplate,
  CreateMessageTemplateRequest,
  UpdateMessageTemplateRequest,
} from '../types/tracker.types';

/**
 * Hook to fetch and manage message templates
 */
export const useMessageTemplates = () => {
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await trackerAPI.getMessageTemplates();
      if (response.success) {
        setTemplates(response.data);
      } else {
        setError('Échec du chargement des modèles');
      }
    } catch (err) {
      console.error('Error fetching message templates:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return {
    templates,
    isLoading,
    error,
    refetch: fetchTemplates,
  };
};

/**
 * Hook to create a new message template
 */
export const useCreateTemplate = () => {
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createTemplate = async (
    template: CreateMessageTemplateRequest
  ): Promise<MessageTemplate | null> => {
    try {
      setIsCreating(true);
      setError(null);
      const response = await trackerAPI.createMessageTemplate(template);

      if (response.success) {
        return response.data;
      } else {
        setError('Échec de la création du modèle');
        return null;
      }
    } catch (err) {
      console.error('Error creating message template:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(errorMessage);
      return null;
    } finally {
      setIsCreating(false);
    }
  };

  return {
    createTemplate,
    isCreating,
    error,
  };
};

/**
 * Hook to update an existing message template
 */
export const useUpdateTemplate = () => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateTemplate = async (
    id: string,
    template: UpdateMessageTemplateRequest
  ): Promise<MessageTemplate | null> => {
    try {
      setIsUpdating(true);
      setError(null);
      const response = await trackerAPI.updateMessageTemplate(id, template);

      if (response.success) {
        return response.data;
      } else {
        setError('Échec de la mise à jour du modèle');
        return null;
      }
    } catch (err) {
      console.error('Error updating message template:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(errorMessage);
      return null;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    updateTemplate,
    isUpdating,
    error,
  };
};

/**
 * Hook to delete a message template
 */
export const useDeleteTemplate = () => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteTemplate = async (id: string): Promise<boolean> => {
    try {
      setIsDeleting(true);
      setError(null);
      const response = await trackerAPI.deleteMessageTemplate(id);

      if (response.success) {
        return true;
      } else {
        setError('Échec de la suppression du modèle');
        return false;
      }
    } catch (err) {
      console.error('Error deleting message template:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(errorMessage);
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    deleteTemplate,
    isDeleting,
    error,
  };
};
